import toFormData from 'json-form-data'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Grid,
} from '@mui/material'
import Stack from '@mui/system/Stack'
import Button from '@mui/material/Button'
import { useMutation } from '@tanstack/react-query'
import { useAPI } from '../../api'
import { useTranslation } from 'react-i18next'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import i18n from '../../i18n'
import { useRef, useState } from 'react'
import { ClearIcon } from '@mui/x-date-pickers'

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { alpha, useTheme, styled } from '@mui/material/styles';

interface NACCSFormValues {
  invoice: File | null
  format: File | null
}

const DropZoneRoot = styled('label')(({ theme }) => ({
  height: '160px',
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  position: 'relative',
  width: '100%',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.dragover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    transform: 'scale(1.02)',
  },
  '&.has-file': {
    borderColor: theme.palette.success.main,
    backgroundColor: alpha(theme.palette.success.main, 0.08),
  },
  '&.is-disabled': {
    borderColor: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
    cursor: 'not-allowed',
  }
}));

export function FileInput({
  name,
  label,
  accept,
  placeholder,
  disabled = false,
}: {
  name: string
  label: string
  accept: string
  placeholder?: string
  disabled?: boolean
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const file = field.value as File | null;

        const handleDragOver = (e: React.DragEvent) => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        };

        const handleDragLeave = () => {
          setIsDragOver(false);
        };

        const handleDrop = (e: React.DragEvent) => {
          e.preventDefault();
          setIsDragOver(false);
          if (disabled) return;
          const droppedFile = e.dataTransfer.files[0];
          if (droppedFile) {
            field.onChange(droppedFile);
          }
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const selectedFile = e.target.files?.[0];
          if (selectedFile) {
            field.onChange(selectedFile);
          }
        };

        const handleClear = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (fileInputRef.current) fileInputRef.current.value = '';
          field.onChange(null);
        };

        return (
          <Stack spacing={1} sx={{ width: '100%' }}>
            {/* ラベル */}
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#263238', fontSize: '1rem', mb: 0.5 }}>
              {label}
            </Typography>

            <DropZoneRoot
              className={`${isDragOver ? 'dragover' : ''} ${file ? 'has-file' : ''} ${disabled ? 'is-disabled' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept={accept}
                onChange={handleChange}
                disabled={disabled}
                style={{ display: 'none' }} 
              />

              <Box sx={{ px: 2, textAlign: 'center', pointerEvents: 'none', position: 'relative', zIndex: 0 }}>
                {!file ? (
                  <Typography variant="body2" color="text.secondary">
                    {placeholder || i18n.t('naccs.fields.placeholder')}
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ color: '#263238', fontWeight: 'bold', wordBreak: 'break-all' }}>
                    {file.name}
                  </Typography>
                )}
              </Box>

              {/* クリアボタン */}
              {file && !disabled && (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </DropZoneRoot>
          </Stack>
        );
      }}
    />
  )
}

const schema = yup
  .object({
    invoice: yup
      .mixed()
      .required(
        i18n.t('validation.required', { field: i18n.t('naccs.fields.invoice') })
      ),
    format: yup.mixed().notRequired(),
  })
  .required()

// フォームコンポーネント（カード単位）
function NACCSForm({ type }: { type: 'import' | 'export' }) {
  const api = useAPI()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme();

  const isImport = type === 'import';
  // モードごとのカラー設定
  const modeColor = isImport ? theme.palette.primary.main : theme.palette.success.main;

  const form = useForm<NACCSFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      invoice: null,
      format: null,
    },
  })

  const exportMutation = useMutation({
    mutationFn: (data: NACCSFormValues) => {
      const fileNameTxt = data.invoice?.name.replace(/\.[^/.]+$/, '.txt')
      return api.toFile(`naccs/?type=${type}`, toFormData(data as any), true, fileNameTxt)
    },
    onSuccess: () => {
      enqueueSnackbar(t('messages.success', { action: t('actions.export') }), {
        variant: 'success',
      })
    },
  })

  const onSubmit = form.handleSubmit((data: NACCSFormValues) =>
    exportMutation.mutate(data)
  )

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%',
        borderTop: `6px solid ${modeColor}`,
        boxShadow: `0 4px 12px ${alpha(modeColor, 0.08)}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          boxShadow: `0 6px 16px ${alpha(modeColor, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <FormProvider {...form}>
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Stack spacing={4} sx={{ flexGrow: 1 }}>
              {/* ヘッダーエリア */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      p: 1, 
                      borderRadius: '50%', 
                      bgcolor: alpha(modeColor, 0.1),
                      color: modeColor 
                    }}
                  >
                    {isImport ? <FileDownloadIcon /> : <FileUploadIcon />}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" color={modeColor}>
                    {isImport ? t('naccs.import') : t('naccs.export')}
                  </Typography>
                </Stack>
              </Box>

              {/* 入力エリア */}
              <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
                <FileInput
                  name="invoice"
                  label={t('naccs.fields.invoice')}
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                />
                <FileInput
                  name="format"
                  label={t('naccs.fields.format')}
                  accept="text/plain"
                />
              </Stack>
              
              {/* 実行ボタン */}
              <Box sx={{ pt: 2, mt: 'auto' }}>
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  size="large"
                  color={isImport ? 'primary' : 'success'}
                  disabled={exportMutation.isPending}
                  sx={{ py: 1.5, fontWeight: 'bold', fontSize: '1rem' }}
                >
                  {t('actions.naccsExport')}
                </Button>
              </Box>
            </Stack>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  )
}

export default function NACCS() {
  const { t } = useTranslation()

  return (
    <Stack spacing={4}>
      <Typography variant="h6">{t('naccs.title')}</Typography>
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} lg={6}>
            <NACCSForm type="import" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <NACCSForm type="export" />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}